import AChart, { type Props as AProps } from "react-apexcharts";
import deepmerge from "deepmerge";
import type { ApexOptions } from "apexcharts";

import numbers, { type NumberFormat } from "core/numbers";


type Props = {
	yFormat?: NumberFormat;
} & AProps;

type GaugeProps = {
	value: number;
	min: number;
	max: number;
	height?: number;
	format?: NumberFormat;
};


const polarToCartesian = (cx: number, cy: number, radius: number, angle: number) => {
	const radians = ((angle - 90) * Math.PI) / 180;
	return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) };
};

const setArc = (cx: number, cy: number, radius: number, startAngle: number, endAngle: number) => {
	const start = polarToCartesian(cx, cy, radius, endAngle);
	const end = polarToCartesian(cx, cy, radius, startAngle);
	const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
	return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
};

Chart.Area = (props: Props) => {
	const options = {
		chart: { type: "area" },
		fill: {
			type: "gradient",
			gradient: {
				shadeIntensity: 1,
				opacityFrom: 0,
				opacityTo: 0.8,
				stops: [0, 100]
			}
		},
		markers: { size: 0 }
	} as ApexOptions;

	const opt = deepmerge(options, props.options || {});

	return props.series && <Chart {...props} options={opt} type="area" />;
};

Chart.Line = (props: Props) => {
	const options = {
		chart: { type: "line" },
		yaxis: {
			min: 0,
			forceNiceScale: true
		},
		markers: { size: 0 }
	} as ApexOptions;

	const opt = deepmerge(options, props.options || {});
	return props.series && <Chart {...props} options={opt} type="line" />;
};

Chart.Bar = (props: Props) => {
	const options = {
		chart: { type: "bar", stacked: true },
		yaxis: {
			min: 0,
			forceNiceScale: true
		}
	} as ApexOptions;

	const opt = deepmerge(options, props.options || {});
	return props.series && <Chart {...props} options={opt} type="bar" />;
};

Chart.Gauge = ({ value, min, max, height = 220, format = "currency" }: GaugeProps) => {
	const GAUGE_START_ANGLE = -90;
	const GAUGE_END_ANGLE = 90;

	const lower = Math.min(min, max);
	const upper = Math.max(min, max);
	const clampedValue = numbers.clamp(value, lower, upper);
	const ratio = upper === lower ? 0 : (clampedValue - lower) / (upper - lower);
	const needleAngle = GAUGE_START_ANGLE + (GAUGE_END_ANGLE - GAUGE_START_ANGLE) * ratio;

	const width = 320;
	const svgHeight = Math.max(160, height - 18);
	const center = { x: 160, y: 128 };
	const radius = 100;
	const ringWidth = 24;
	const segmentGap = 2.5;
	const segments = ["#a9382f", "#d96a22", "#e98d25", "#f1c232", "#c4ce33", "#8db337", "#5d993f", "#3f7f47"];
	const segmentSweep = (GAUGE_END_ANGLE - GAUGE_START_ANGLE) / segments.length;

	const getLabel = (value: number, decimals = 0) => numbers.format(value, format, { notation: "compact", decimals });

	// Needle
	const needle = {
		start: polarToCartesian(center.x, center.y, radius - ringWidth - 3, needleAngle),
		end: polarToCartesian(center.x, center.y, radius - ringWidth / 2 + 18, needleAngle)
	};

	const needleDeltaX = needle.end.x - needle.start.x;
	const needleDeltaY = needle.end.y - needle.start.y;
	const needleLength = Math.hypot(needleDeltaX, needleDeltaY) || 1;
	const needleNormalX = -needleDeltaY / needleLength;
	const needleNormalY = needleDeltaX / needleLength;
	const needleSize = { base: 5, tip: 2 };

	const needleBaseLeft = { x: needle.start.x + needleNormalX * needleSize.base, y: needle.start.y + needleNormalY * needleSize.base };
	const needleBaseRight = { x: needle.start.x - needleNormalX * needleSize.base, y: needle.start.y - needleNormalY * needleSize.base };
	const needleTipLeft = { x: needle.end.x + needleNormalX * needleSize.tip, y: needle.end.y + needleNormalY * needleSize.tip };
	const needleTipRight = { x: needle.end.x - needleNormalX * needleSize.tip, y: needle.end.y - needleNormalY * needleSize.tip };

	const needlePath = `M ${needleBaseLeft.x} ${needleBaseLeft.y} L ${needleBaseRight.x} ${needleBaseRight.y} L ${needleTipRight.x} ${needleTipRight.y} L ${needleTipLeft.x} ${needleTipLeft.y} Z`;
	const minPoint = polarToCartesian(center.x, center.y, radius, GAUGE_START_ANGLE + segmentGap / 2);
	const maxPoint = polarToCartesian(center.x, center.y, radius, GAUGE_END_ANGLE - segmentGap / 2);
	const labelY = Math.max(minPoint.y, maxPoint.y) + 20;

	return (
		<svg viewBox={`0 0 ${width} ${svgHeight}`} style={{ width: "100%" }} aria-label="Gauge chart">
			<defs>
				<filter id="gaugeNeedleShadow" x="-30%" y="-30%" width="160%" height="160%">
					<feDropShadow dx="1.5" dy="1.5" stdDeviation="1.2" floodColor="#000000" floodOpacity="0.35" />
				</filter>
			</defs>

			<path d={setArc(center.x, center.y, radius, GAUGE_START_ANGLE, GAUGE_END_ANGLE)} fill="none" strokeWidth={ringWidth + 6} strokeLinecap="round"/>

			{segments.map((color, index) => {
				const startAngle = GAUGE_START_ANGLE + index * segmentSweep + segmentGap / 2;
				const endAngle = GAUGE_START_ANGLE + (index + 1) * segmentSweep - segmentGap / 2;
				return (
					<path
						key={`${color}-${index}`}
						d={setArc(center.x, center.y, radius, startAngle, endAngle)}
						fill="none"
						stroke={color}
						strokeWidth={ringWidth}
						strokeLinecap="butt"
					/>
				);
			})}

			<path d={needlePath} fill="#d4deec" filter="url(#gaugeNeedleShadow)" />

			<text x={center.x} y={108} fill="#1b2633" textAnchor="middle" style={{ fontSize: "32px", fontWeight: 700, letterSpacing: "-1px" }}>
				{getLabel(clampedValue)}
			</text>

			<g>
				<text x={minPoint.x} y={labelY} textAnchor="start" fill="#27354a" style={{ fontSize: "18px", fontWeight: 700, transform: "translateX(-18px)" }}>
					{getLabel(lower)}
				</text>
				<text x={maxPoint.x} y={labelY} textAnchor="end" fill="#27354a" style={{ fontSize: "18px", fontWeight: 700, transform: "translateX(18px)" }}>
					{getLabel(upper)}
				</text>
			</g>
		</svg>
	);
};

export default function Chart({ colors, yFormat = "number", ...props }: Props) {
	const options = {
		chart: {
			toolbar: { show: false }
		},
		legend: { show: false },
		dataLabels: {
			enabled: false,
			style: { colors: colors }
		},
		colors: colors,
		xaxis: {
			type: "datetime",
			labels: {
				format: "MMM dd"
			}
		},
		yaxis: {
			labels: { formatter: (e) => numbers.format(e, yFormat) }
		},
		tooltip: {
			y: { formatter: (e) => numbers.format(e, yFormat) }
		}
	} as ApexOptions;

	const opt = deepmerge(options, props.options || {});

	return <AChart height={220} {...props} options={opt} />;
}